import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Users, Trophy, Calendar, MessageSquare, LogOut, DollarSign, CreditCard, MapPin, UserPlus, FileText, Bell, ClipboardCheck, TrendingUp, LayoutDashboard, AlertTriangle, Receipt, Award, Shirt } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AcademyDashboard({ user, logout }) {
  const isParent = user.roles && user.roles.includes("academy_parent");
  const isPlayer = user.roles && user.roles.includes("academy_player");
  const isCoach = user.roles && user.roles.includes("academy_coach");
  const isAdmin = ["convenor", "captain"].includes(user.role);

  // Fetch an open term so the Register card shows "Register for Term X" when applicable
  const [openTerm, setOpenTerm] = useState(null);
  useEffect(() => {
    if (!isParent) return;
    axios.get(`${API}/academy/terms`, { withCredentials: true })
      .then((r) => {
        const now = new Date();
        const terms = (r.data || []).filter((t) => t.is_finalized);
        // Pick term whose registration window is currently open, else the next upcoming
        const open = terms.find((t) => {
          if (!t.registration_opens_at || !t.registration_closes_at) return false;
          const openAt = new Date(t.registration_opens_at);
          const closeAt = new Date(t.registration_closes_at);
          return openAt <= now && now <= closeAt;
        }) || terms.find((t) => {
          if (!t.start_date) return false;
          return new Date(t.start_date) > now;
        });
        if (open) setOpenTerm(open);
      })
      .catch(() => setOpenTerm(null));
  }, [isParent]);

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" className="text-slate-400 hover:text-white hover:bg-slate-900/50/5">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Main
              </Button>
            </Link>
            <div className="flex items-center gap-2.5">
              <img src="/branding/football-and-more-white.png" alt="Football And More" className="h-8 md:h-9 w-auto" />
              <span className="h-6 w-px bg-slate-600" aria-hidden="true"></span>
              <span className="text-base md:text-lg font-bold text-blue-400 tracking-wide font-display">SCC Football Academy</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-300 text-sm hidden sm:inline">Welcome, {user.name}</span>
            <NotificationBell user={user} />
            <Button
              onClick={logout}
              variant="outline"
              size="sm"
              className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2 font-display">Academy Dashboard</h2>
          <p className="text-slate-400">Youth development & training portal</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Register for Term — existing or new child */}
          {isParent && (
            <Link to="/academy/start-registration" data-testid="register-for-term-card">
              <Card className="p-8 bg-slate-900/50 border border-emerald-500/30 hover:border-emerald-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-emerald-500/20 rounded-full mb-4">
                  <UserPlus className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors">
                  {openTerm ? `Register for ${openTerm.name}` : "Register for Term"}
                </h3>
                <p className="text-slate-400">Enroll existing or new children for the upcoming term</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className="inline-block text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">Term Sign-up</span>
                  <span className="inline-block text-xs bg-emerald-500/10 text-emerald-300/80 px-2 py-1 rounded">+ Add Child</span>
                </div>
              </Card>
            </Link>
          )}

          {/* Register as a coach — right beside registering the kids, so a
              member never has to sign out to find the public form. */}
          {!isCoach && (
            <Link to="/coach-application" data-testid="academy-coach-apply-card">
              <Card className="p-8 bg-slate-900/50 border border-yellow-500/30 hover:border-yellow-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-yellow-500/20 rounded-full mb-4">
                  <UserPlus className="w-8 h-8 text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-yellow-400 transition-colors">
                  Register as a Coach
                </h3>
                <p className="text-slate-400">Apply to coach — we use the login you already have</p>
                <span className="inline-block text-xs bg-yellow-500/20 text-yellow-300 px-2 py-1 rounded mt-2">
                  No second account
                </span>
              </Card>
            </Link>
          )}

          {/* Parent Dashboard */}
          {isParent && (
            <Link to="/academy/parent-dashboard">
              <Card className="p-8 bg-slate-900/50 border border-purple-500/30 hover:border-purple-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-purple-500/20 rounded-full mb-4">
                  <LayoutDashboard className="w-8 h-8 text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">My Dashboard</h3>
                <p className="text-slate-400">Overview of children, sessions & payments</p>
              </Card>
            </Link>
          )}

          {/* Coach Dashboard */}
          {isCoach && (
            <Link to="/academy/coach-dashboard">
              <Card className="p-8 bg-slate-900/50 border border-teal-500/30 hover:border-teal-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-teal-500/20 rounded-full mb-4">
                  <LayoutDashboard className="w-8 h-8 text-teal-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-teal-400 transition-colors">Coach Dashboard</h3>
                <p className="text-slate-400">Teams, sessions & attendance</p>
              </Card>
            </Link>
          )}

          {/* My Kids - Parents Only */}
          {isParent && (
            <Link to="/academy/my-kids">
              <Card className="p-8 bg-slate-900/50 border border-blue-500/30 hover:border-blue-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-blue-500/20 rounded-full mb-4">
                  <Users className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">My Kids</h3>
                <p className="text-slate-400">View your children&apos;s profiles & stats</p>
              </Card>
            </Link>
          )}

          {/* Session Upgrade - Parents Only */}
          {isParent && (
            <Link to="/academy/session-upgrade">
              <Card className="p-8 bg-slate-900/50 border border-emerald-500/30 hover:border-emerald-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-emerald-500/20 rounded-full mb-4">
                  <TrendingUp className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors">Add Sessions</h3>
                <p className="text-slate-400">Add sessions or upgrade mid-term</p>
                <span className="inline-block mt-2 text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">Pro-rata Available</span>
              </Card>
            </Link>
          )}

          {/* My Invoices - Parents Only */}
          {isParent && (
            <Link to="/academy/my-invoices">
              <Card className="p-8 bg-slate-900/50 border border-indigo-500/30 hover:border-indigo-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-indigo-500/20 rounded-full mb-4">
                  <Receipt className="w-8 h-8 text-indigo-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-indigo-400 transition-colors">My Invoices</h3>
                <p className="text-slate-400">Download payment invoices</p>
                <span className="inline-block mt-2 text-xs bg-indigo-500/20 text-indigo-400 px-2 py-1 rounded">PDF Download</span>
              </Card>
            </Link>
          )}

          {/* Kit Orders - Parents Only (always visible) */}
          {isParent && (
            <Link to="/academy/kit-orders" data-testid="parent-kit-orders-tile">
              <Card className="p-8 bg-slate-900/50 border border-fuchsia-500/30 hover:border-fuchsia-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-fuchsia-500/20 rounded-full mb-4">
                  <Shirt className="w-8 h-8 text-fuchsia-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-fuchsia-400 transition-colors">Kit Orders</h3>
                <p className="text-slate-400">Order initial kit, replacements or extras</p>
                <span className="inline-block mt-2 text-xs bg-fuchsia-500/20 text-fuchsia-400 px-2 py-1 rounded">Kit & Jersey</span>
              </Card>
            </Link>
          )}

          {/* My Dashboard - Players Only */}
          {isPlayer && (
            <Link to="/academy/player-dashboard">
              <Card className="p-8 bg-slate-900/50 border border-emerald-500/30 hover:border-emerald-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-emerald-500/20 rounded-full mb-4">
                  <Trophy className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors">My Profile</h3>
                <p className="text-slate-400">View your stats & achievements</p>
              </Card>
            </Link>
          )}

          {/* Rate Tables - Admin Only */}
          {isAdmin && (
            <Link to="/academy/rate-tables">
              <Card className="p-8 bg-slate-900/50 border border-emerald-500/30 hover:border-emerald-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-emerald-500/20 rounded-full mb-4">
                  <DollarSign className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors">Rate Tables</h3>
                <p className="text-slate-400">Manage pricing & discount structures</p>
              </Card>
            </Link>
          )}

          {/* Term Management - Admin Only */}
          {isAdmin && (
            <Link to="/academy/term-management">
              <Card className="p-8 bg-slate-900/50 border border-purple-500/30 hover:border-purple-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-purple-500/20 rounded-full mb-4">
                  <Calendar className="w-8 h-8 text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">Terms & Sessions</h3>
                <p className="text-slate-400">Create terms & schedule sessions</p>
              </Card>
            </Link>
          )}

          {/* Payment Verification - Admin Only */}
          {isAdmin && (
            <Link to="/academy/payment-verification">
              <Card className="p-8 bg-slate-900/50 border border-yellow-500/30 hover:border-yellow-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-yellow-500/20 rounded-full mb-4">
                  <CreditCard className="w-8 h-8 text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-yellow-400 transition-colors">Payment Verification</h3>
                <p className="text-slate-400">Review & confirm PayNow payments</p>
              </Card>
            </Link>
          )}

          {/* Unassigned Players - Admin Only */}
          {isAdmin && (
            <Link to="/academy/unassigned-players">
              <Card className="p-8 bg-slate-900/50 border border-orange-500/30 hover:border-orange-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-orange-500/20 rounded-full mb-4">
                  <UserPlus className="w-8 h-8 text-orange-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-orange-400 transition-colors">Unassigned Players</h3>
                <p className="text-slate-400">Assign new players to teams</p>
              </Card>
            </Link>
          )}

          {/* Player Overview - Admin Only */}
          {isAdmin && (
            <Link to="/academy/player-overview">
              <Card className="p-8 bg-slate-900/50 border border-cyan-500/30 hover:border-cyan-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-cyan-500/20 rounded-full mb-4">
                  <Users className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">Player Overview</h3>
                <p className="text-slate-400">All players, sessions, parents & Telegram status</p>
              </Card>
            </Link>
          )}

          {/* Coaches Management - Admin Only */}
          {isAdmin && (
            <Link to="/academy/coach-management">
              <Card className="p-8 bg-slate-900/50 border border-orange-500/30 hover:border-orange-500 rounded-xl transition-all cursor-pointer group" data-testid="coaches-card">
                <div className="flex items-center justify-center w-14 h-14 bg-orange-500/20 rounded-full mb-4">
                  <Award className="w-8 h-8 text-orange-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-orange-400 transition-colors">Coaches</h3>
                <p className="text-slate-400">Manage coaches & team assignments</p>
                <span className="inline-block mt-2 text-xs bg-orange-500/20 text-orange-400 px-2 py-1 rounded">Admin</span>
              </Card>
            </Link>
          )}

          {/* Locations Management - Admin Only */}
          {isAdmin && (
            <Link to="/academy/locations">
              <Card className="p-8 bg-slate-900/50 border border-emerald-500/30 hover:border-emerald-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-emerald-500/20 rounded-full mb-4">
                  <MapPin className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors">Training Locations</h3>
                <p className="text-slate-400">Manage training venues</p>
              </Card>
            </Link>
          )}

          {/* Consent Text Management - Admin Only */}
          {isAdmin && (
            <Link to="/academy/consent-texts">
              <Card className="p-8 bg-slate-900/50 border border-amber-500/30 hover:border-amber-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-amber-500/20 rounded-full mb-4">
                  <FileText className="w-8 h-8 text-amber-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">Consent Forms</h3>
                <p className="text-slate-400">Edit PDPA, Safe Sport & Indemnity</p>
              </Card>
            </Link>
          )}

          {/* Registration Notice Management - Admin Only */}
          {isAdmin && (
            <Link to="/academy/registration-notice-management">
              <Card className="p-8 bg-slate-900/50 border border-rose-500/30 hover:border-rose-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-rose-500/20 rounded-full mb-4">
                  <Bell className="w-8 h-8 text-rose-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-rose-400 transition-colors">Registration Notice</h3>
                <p className="text-slate-400">Important notice for parents</p>
              </Card>
            </Link>
          )}

          {/* Admin Approvals - Admin Only */}
          {isAdmin && (
            <Link to="/admin/approvals">
              <Card className="p-8 bg-slate-900/50 border border-yellow-500/30 hover:border-yellow-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-yellow-500/20 rounded-full mb-4">
                  <ClipboardCheck className="w-8 h-8 text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-yellow-400 transition-colors">Approvals & Payments</h3>
                <p className="text-slate-400">Review registrations, payments & outstanding fees</p>
                <span className="inline-block mt-2 text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">Admin</span>
              </Card>
            </Link>
          )}

          {/* Session Management - Admin/Coach Only */}
          {(isAdmin || isCoach) && (
            <Link to="/academy/session-management">
              <Card className="p-8 bg-slate-900/50 border border-red-500/30 hover:border-red-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-red-500/20 rounded-full mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-red-400 transition-colors">Weekly Schedule</h3>
                <p className="text-slate-400">The weekly slots: cancel, reschedule, change venue, announce</p>
                <span className="inline-block mt-2 text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded">Admin/Coach</span>
              </Card>
            </Link>
          )}

          {/* Academy Teams */}
          <Link to="/academy/teams">
            <Card className="p-8 bg-slate-900/50 border border-blue-500/30 hover:border-blue-500 rounded-xl transition-all cursor-pointer group">
              <div className="flex items-center justify-center w-14 h-14 bg-blue-500/20 rounded-full mb-4">
                <Users className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">Teams</h3>
              <p className="text-slate-400">
                {isParent ? "View my kids' teams" : "Browse academy teams"}
              </p>
            </Card>
          </Link>

          {/* Training */}
          <Link to="/academy/sessions">
            <Card className="p-8 bg-slate-900/50 border border-blue-500/30 hover:border-blue-500 rounded-xl transition-all cursor-pointer group">
              <div className="flex items-center justify-center w-14 h-14 bg-blue-500/20 rounded-full mb-4">
                <Calendar className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">
                {isAdmin || isCoach ? "Sessions & Attendance" : "Training"}
              </h3>
              <p className="text-slate-400">
                {isAdmin || isCoach
                  ? "Every dated session by term and day — mark the register here"
                  : "View schedules & sign up"}
              </p>
            </Card>
          </Link>

          {/* Attendance — registers, who attended, and what it's worth */}
          {(isAdmin || isCoach) && (
            <Link to="/academy/attendance" data-testid="academy-dashboard-attendance-link">
              <Card className="p-8 bg-slate-900/50 border border-emerald-500/30 hover:border-emerald-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-emerald-500/20 rounded-full mb-4">
                  <ClipboardCheck className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors">
                  Attendance
                </h3>
                <p className="text-slate-400">
                  {isAdmin
                    ? "Who attended, download it, and what each session is worth"
                    : "Check in and mark your registers"}
                </p>
              </Card>
            </Link>
          )}

          {/* My pay — a coach's own sessions and his invoice to the club */}
          {isCoach && !isAdmin && (
            <Link to="/academy/my-pay" data-testid="academy-dashboard-my-pay-link">
              <Card className="p-8 bg-slate-900/50 border border-amber-500/30 hover:border-amber-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-amber-500/20 rounded-full mb-4">
                  <Receipt className="w-8 h-8 text-amber-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">
                  My Pay
                </h3>
                <p className="text-slate-400">
                  Your sessions, what you're owed, and your invoice to Football &amp; More
                </p>
              </Card>
            </Link>
          )}

          {/* Games — every game, league or friendly */}
          {!isParent && !isPlayer && (
            <Link to="/academy/fixtures" data-testid="academy-dashboard-games-link">
              <Card className="p-8 bg-slate-900/50 border border-amber-500/30 hover:border-amber-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-amber-500/20 rounded-full mb-4">
                  <Trophy className="w-8 h-8 text-amber-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">Games</h3>
                <p className="text-slate-400">Fixtures, squads, results &amp; the table</p>
              </Card>
            </Link>
          )}

          {/* Coach Feedback - Parents & Players */}
          {(isParent || isPlayer) && (
            <Link to="/academy/feedback">
              <Card className="p-8 bg-slate-900/50 border border-purple-500/30 hover:border-purple-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-purple-500/20 rounded-full mb-4">
                  <MessageSquare className="w-8 h-8 text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">Coach Feedback</h3>
                <p className="text-slate-400">View performance feedback</p>
              </Card>
            </Link>
          )}

          {/* Browse All Teams - Players Only */}
          {isPlayer && (
            <Link to="/academy/browse-teams">
              <Card className="p-8 bg-slate-900/50 border border-yellow-500/30 hover:border-yellow-500 rounded-xl transition-all cursor-pointer group">
                <div className="flex items-center justify-center w-14 h-14 bg-yellow-500/20 rounded-full mb-4">
                  <Trophy className="w-8 h-8 text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-yellow-400 transition-colors">All Teams & Results</h3>
                <p className="text-slate-400">Browse academy-wide results</p>
              </Card>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
